import { useMutation, type UseMutationOptions, useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ApiClient } from './api.client.js';
import { type CodeCategory, type CreateCodeCategory } from '../models/index.js';

export const CategoriesApiKeys = {
  GET_CATEGORY: 'GET_CATEGORY',
  GET_CATEGORIES: 'GET_CATEGORIES',
  CREATE_CATEGORY: 'CREATE_CATEGORY',
  UPDATE_CATEGORY: 'UPDATE_CATEGORY',
  DELETE_CATEGORY: 'DELETE_CATEGORY',
};

export class CategoriesApi extends ApiClient {
  public static async getCategory(id: string): Promise<CodeCategory> {
    return this.http.get(`/categories/${id}`).then((res) => res.data);
  }

  public static async getAllCategories(): Promise<Array<CodeCategory>> {
    return this.http.get(`/categories`).then((res) => res.data);
  }

  public static async createCategory(category: CreateCodeCategory): Promise<CodeCategory> {
    return this.http.post('/categories', category);
  }

  public static async updateCategory(category: CodeCategory): Promise<CodeCategory> {
    return this.http.put(`/categories/${category.id}`, category);
  }

  public static async deleteCategory(categoryId: string): Promise<void> {
    return this.http.delete(`/categories/${categoryId}`);
  }
}

export function useCategory(id: string, queryOptions?: UseQueryOptions<CodeCategory>) {
  return useQuery<CodeCategory>([CategoriesApiKeys.GET_CATEGORY, id], () => CategoriesApi.getCategory(id), {
    cacheTime: 0,
    ...queryOptions,
  });
}

export function useAllCategories(queryOptions?: UseQueryOptions<Array<CodeCategory>>) {
  return useQuery<Array<CodeCategory>>(
    [CategoriesApiKeys.GET_CATEGORIES],
    async () => CategoriesApi.getAllCategories(),
    queryOptions,
  );
}

export function useMutateCreateCategory(mutationOptions?: UseMutationOptions<CodeCategory, unknown, CreateCodeCategory>) {
  return useMutation(
    [CategoriesApiKeys.CREATE_CATEGORY],
    (category: CreateCodeCategory) => CategoriesApi.createCategory(category),
    mutationOptions,
  );
}

export function useMutateUpdateCategory(mutationOptions?: UseMutationOptions<CodeCategory, unknown, CodeCategory>) {
  return useMutation(
    [CategoriesApiKeys.UPDATE_CATEGORY],
    (category: CodeCategory) => CategoriesApi.updateCategory(category),
    mutationOptions,
  );
}

export function useMutateDeleteCategory(mutationOptions?: UseMutationOptions<void, unknown, string>) {
  return useMutation(
    [CategoriesApiKeys.DELETE_CATEGORY],
    (categoryId: string) => CategoriesApi.deleteCategory(categoryId),
    mutationOptions,
  );
}
